/**
 * Inactivity Logout Hook
 * Automatically logs out the user after a period of inactivity
 *
 * How it works:
 * - Tracks user activity (mouse, keyboard, scroll, touch)
 * - Resets timer on any activity
 * - Logs out and redirects to login after timeout
 *
 * Configuration:
 * - Production: 4 hours (configurable)
 * - Development: 8 hours (more relaxed for devs)
 */

import { useEffect, useRef, useCallback } from 'react';
import { firebaseAuthService } from '@/services/firebase/firebaseAuthService';

// Inactivity timeout in milliseconds
const INACTIVITY_TIMEOUT_PROD = 4 * 60 * 60 * 1000; // 4 hours
const INACTIVITY_TIMEOUT_DEV = 8 * 60 * 60 * 1000;  // 8 hours

const INACTIVITY_TIMEOUT = import.meta.env.PROD
  ? INACTIVITY_TIMEOUT_PROD
  : INACTIVITY_TIMEOUT_DEV;

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
  'click',
] as const;

interface UseInactivityLogoutOptions {
  /** Whether the hook is enabled (usually when user is authenticated) */
  enabled: boolean;
  /** Custom timeout in milliseconds (optional) */
  timeout?: number;
  /** Callback before logout (optional) */
  onBeforeLogout?: () => void;
}

export function useInactivityLogout({
  enabled,
  timeout = INACTIVITY_TIMEOUT,
  onBeforeLogout,
}: UseInactivityLogoutOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const handleLogout = useCallback(async () => {
    // Don't logout from within an iframe (e.g., heatmap preview)
    if (window.self !== window.top) return;

    console.log('⏱️ [Inactivity] Logging out due to inactivity');

    // Call optional callback
    onBeforeLogout?.();

    try {
      // Sign out from Firebase
      await firebaseAuthService.signOut();
    } catch (error) {
      console.error('❌ [Inactivity] Error during logout:', error);
    }

    // Redirect to login
    window.location.href = '/login';
  }, [onBeforeLogout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timer
    timeoutRef.current = setTimeout(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      if (inactiveTime >= timeout) {
        handleLogout();
      }
    }, timeout);
  }, [timeout, handleLogout]);

  useEffect(() => {
    if (!enabled) {
      // Clear timer if disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Start initial timer
    resetTimer();

    // Add activity listeners
    const handleActivity = () => {
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we should have logged out while tab was inactive
        const inactiveTime = Date.now() - lastActivityRef.current;
        if (inactiveTime >= timeout) {
          handleLogout();
        } else {
          resetTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, timeout, resetTimer, handleLogout]);

  // Return time remaining for debugging (dev only)
  const getTimeRemaining = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = Math.max(0, timeout - elapsed);
    return {
      remainingMs: remaining,
      remainingMinutes: Math.floor(remaining / 60000),
      remainingHours: Math.floor(remaining / 3600000),
    };
  }, [timeout]);

  return { getTimeRemaining };
}
